import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { SubPageHeader } from "@/components/SubPageHeader";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Chip } from "@/components/Chip";
import { Plus, X, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, todayISO } from "@/lib/date";

const TYPES = ["national", "regional", "optional", "company"];

export default function Holidays() {
  const { user } = useAuth();
  const { settings } = useSettings();
  const [hols, setHols] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", date: todayISO(), type: "company" });

  const load = async () => { try { const { data } = await api.get("/holidays"); setHols(data); } catch {} };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try { await api.post("/holidays", form); toast.success("Added"); setOpen(false); setForm({ name: "", date: todayISO(), type: "company" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/holidays/${id}`); load(); } catch { toast.error("Failed"); }
  };

  const canManage = user?.role === "admin" || (user?.role === "hr_manager" && !!settings?.hr_can_edit_holidays);

  return (
    <div className="min-h-[100svh]" style={{ background: "#0A0D14", paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="holidays-page">
      <SubPageHeader title="Holidays" right={canManage && (
        <button onClick={() => setOpen(true)} data-testid="add-holiday-button" className="h-9 px-3 rounded-lg flex items-center gap-1 text-xs font-bold" style={{ background: "#9B6FEB", color: "#fff" }}>
          <Plus size={14} /> Add
        </button>
      )} />
      <div className="px-4 py-4 space-y-2">
        {hols.length === 0 && <div className="text-center text-sm py-8" style={{ color: "#7A85A3" }}>No holidays</div>}
        {hols.map((h) => (
          <div key={h.id} className="rounded-2xl p-4 flex items-center gap-3" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid={`holiday-${h.id}`}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(155,111,235,0.15)", color: "#9B6FEB" }}>
              <Star size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate" style={{ color: "#E8ECFF" }}>{h.name}</div>
              <div className="text-xs" style={{ color: "#7A85A3" }}>{fmtDate(h.date, "EEE, d MMM yyyy")}</div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Chip color="purple">{h.type}</Chip>
              {canManage && (
                <button onClick={() => remove(h.id)} data-testid={`delete-holiday-${h.id}`} className="text-xs flex items-center gap-1" style={{ color: "#F75A5A" }}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent style={{ background: "#111520", borderColor: "#1E2640" }}>
          <div className="mx-auto w-12 h-1.5 rounded-full mt-2 mb-3" style={{ background: "#1E2640" }} />
          <div className="px-6 pb-8 space-y-3" data-testid="add-holiday-form">
            <div className="flex items-center justify-between mb-2">
              <div className="font-syne text-xl font-bold tracking-tight" style={{ color: "#E8ECFF" }}>Add Holiday</div>
              <button onClick={() => setOpen(false)} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#161B2C", border: "1px solid #1E2640", color: "#7A85A3" }}><X size={16} /></button>
            </div>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Holiday name" data-testid="holiday-name"
              className="w-full h-11 px-3 rounded-xl" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }} />
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="holiday-date"
              className="w-full h-11 px-3 rounded-xl" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }} />
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="holiday-type"
              className="w-full h-11 px-3 rounded-xl text-sm" style={{ background: "#0A0D14", border: "1px solid #1E2640", color: "#E8ECFF" }}>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <button onClick={submit} data-testid="holiday-submit" className="w-full h-12 rounded-xl font-bold" style={{ background: "#9B6FEB", color: "#fff" }}>Add Holiday</button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
